import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Select from 'react-select';
import { 
  DollarSign, 
  Plus, 
  Edit2, 
  Trash2, 
  Search,
  School,
  BookOpen,
  Clock,
  AlertTriangle,
  X,
  Filter,
  ArrowLeft
} from 'lucide-react';
import api from '../components/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const TabButton = ({ isActive, onClick, icon: Icon, children }) => (
  <button
    onClick={onClick}
    className={`
      flex-1 px-4 py-4 text-sm font-medium relative transition-all duration-300 ease-in-out
      ${isActive 
        ? 'text-[#202654] bg-white' 
        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
      }
    `}
  >
    <div className="flex items-center justify-center space-x-2">
      <Icon className={`h-5 w-5 transition-colors duration-300 ${isActive ? 'text-[#202654]' : 'text-gray-400'}`} />
      <span>{children}</span>
    </div>
    {isActive && (
      <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#202654] transform scale-x-100 transition-transform duration-300" />
    )}
  </button>
);

const TarificationPage = () => {
  const navigate = useNavigate();
  // États pour les différents types de tarifs
  const [tarifs, setTarifs] = useState([]);
  const [tarifsComplementaires, setTarifsComplementaires] = useState([]);
  const [tarifsVacation, setTarifsVacation] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [anneeEnCours, setAnneeEnCours] = useState(null);
  const [activeTab, setActiveTab] = useState('tarifs');
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTarif, setSelectedTarif] = useState(null);
  const [formData, setFormData] = useState({
    prix: '',
    niveau: '',
    filiere: [],
    libelle: '',
    prix_heure: ''
  });
  const [pagination, setPagination] = useState({
    tarifs: { page: 1, totalPages: 1 },
    complementaires: { page: 1, totalPages: 1 },
    vacation: { page: 1, totalPages: 1 }
  });
  const [searchQueries, setSearchQueries] = useState({
    tarifs: '',
    complementaires: '',
    vacation: ''
  });
  const [filteredData, setFilteredData] = useState({
    tarifs: [],
    complementaires: [],
    vacation: []
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    item: null,
    type: null
  });

  // Style personnalisé pour react-select
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#202654' : '#E5E7EB',
      boxShadow: state.isFocused ? '0 0 0 1px #202654' : base.boxShadow,
      '&:hover': {
        borderColor: '#202654'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#202654' : state.isFocused ? 'rgba(32, 38, 84, 0.1)' : base.backgroundColor,
      color: state.isSelected ? 'white' : base.color,
      '&:hover': {
        backgroundColor: state.isSelected ? '#202654' : 'rgba(32, 38, 84, 0.2)'
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: 'rgba(32, 38, 84, 0.1)'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#202654'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#202654',
      '&:hover': {
        backgroundColor: '#202654',
        color: 'white'
      }
    })
  };

  useEffect(() => {
    // Récupérer l'onglet actif depuis le localStorage
    const savedTab = localStorage.getItem('tarificationActiveTab');
    if (savedTab) {
      setActiveTab(savedTab);
    }
    fetchInitialData();
  }, []);

  // Mettre à jour le localStorage quand l'onglet change
  useEffect(() => {
    localStorage.setItem('tarificationActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    filterData();
  }, [searchQueries, tarifs, tarifsComplementaires, tarifsVacation]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [anneeResponse, niveauxResponse, filieresResponse] = await Promise.all([
        api.get('/etablissement/annees-academiques/'),
        api.get('/niveau/liste-niveaux/'),
        api.get('/filiere/liste-filieres-all/')
      ]);

      const anneeActive = anneeResponse.data.find(annee => annee.status === true);
      if (anneeActive) {
        setAnneeEnCours(anneeActive);
        fetchTarifs(anneeActive.id);
      } else {
        toast.warning('Aucune année académique active');
      }

      // Mise à jour des états avec les données
      setNiveaux(Array.isArray(niveauxResponse.data) ? niveauxResponse.data : []);
      setFilieres(Array.isArray(filieresResponse.data) ? filieresResponse.data : []);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast.error('Erreur lors du chargement des données');
      setNiveaux([]);
      setFilieres([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTarifs = async (anneeId) => {
    try {
      const [tarifsResponse, tarifsCompResponse, tarifsVacResponse] = await Promise.all([
        api.get(`/niveau/liste-tarifs/?page=${pagination.tarifs.page}`),
        api.get(`/niveau/liste-tarifs-complementaires/?page=${pagination.complementaires.page}`),
        api.get(`/niveau/liste-tarifs-vacation/?page=${pagination.vacation.page}`)
      ]);

      setTarifs(tarifsResponse.data.results);
      setTarifsComplementaires(tarifsCompResponse.data.results);
      setTarifsVacation(tarifsVacResponse.data.results);

      setPagination({
        tarifs: { 
          page: pagination.tarifs.page,
          totalPages: Math.ceil(tarifsResponse.data.count / 9)
        },
        complementaires: {
          page: pagination.complementaires.page,
          totalPages: Math.ceil(tarifsCompResponse.data.count / 9)
        },
        vacation: {
          page: pagination.vacation.page,
          totalPages: Math.ceil(tarifsVacResponse.data.count / 9)
        }
      });
    } catch (error) {
      console.error('Erreur lors du chargement des tarifs:', error);
      toast.error('Erreur lors du chargement des tarifs');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!anneeEnCours) {
      toast.error('Aucune année académique active');
      return;
    }

    try {
      setLoading(true);
      const dataToSend = {
        ...formData,
        annee: anneeEnCours.id
      };

      let response;
      if (activeTab === 'tarifs') {
        if (selectedTarif) {
          response = await api.put(`/niveau/details-tarifs/${selectedTarif.id}/`, dataToSend);
          toast.success('Tarif mis à jour avec succès');
        } else {
          response = await api.post('/niveau/liste-tarifs/', dataToSend);
          toast.success('Tarif ajouté avec succès');
        }
      } else if (activeTab === 'complementaires') {
        if (selectedTarif) {
          response = await api.put(`/niveau/details-tarifs-complementaires/${selectedTarif.id}/`, dataToSend);
          toast.success('Tarif complémentaire mis à jour avec succès');
        } else {
          response = await api.post('/niveau/liste-tarifs-complementaires/', dataToSend);
          toast.success('Tarif complémentaire ajouté avec succès');
        }
      } else if (activeTab === 'vacation') {
        if (selectedTarif) {
          response = await api.put(`/niveau/details-tarifs-vacation/${selectedTarif.id}/`, dataToSend);
          toast.success('Tarif de vacation mis à jour avec succès');
        } else {
          response = await api.post('/niveau/liste-tarifs-vacation/', dataToSend);
          toast.success('Tarif de vacation ajouté avec succès');
        }
      }

      fetchTarifs(anneeEnCours.id);
      handleCloseModal();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      if (error.response?.data) {
        const errors = error.response.data;
        
        // Gestion spécifique des erreurs de validation
        if (errors.non_field_errors) {
          errors.non_field_errors.forEach(error => {
            toast.error(error, {
              autoClose: 8000,
              style: {
                backgroundColor: '#FEF2F2',
                color: '#991B1B',
                borderLeft: '4px solid #DC2626',
                maxWidth: '600px',
                whiteSpace: 'pre-wrap'
              },
              position: "top-center"
            });
          });
        } else if (typeof errors === 'object') {
          Object.entries(errors).forEach(([field, message]) => {
            const fieldName = field === 'prix' ? 'Prix' :
                            field === 'niveau' ? 'Niveau' :
                            field === 'filiere' ? 'Filière(s)' :
                            field === 'libelle' ? 'Libellé' :
                            field === 'prix_heure' ? 'Prix horaire' : field;
            
            toast.error(`${fieldName}: ${Array.isArray(message) ? message.join(', ') : message}`, {
              autoClose: 6000,
              style: {
                backgroundColor: '#FEF2F2',
                color: '#991B1B',
                maxWidth: '600px'
              },
              position: "top-center"
            });
          });
        }
      } else {
        toast.error('Une erreur est survenue lors de la sauvegarde', {
          autoClose: 4000,
          position: "top-center"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (item, type) => {
    setDeleteModal({
      isOpen: true,
      item,
      type
    });
  };

  const confirmDelete = async () => {
    const { item, type } = deleteModal;
    try {
      let endpoint;
      switch (type) {
        case 'tarifs':
          endpoint = `/niveau/details-tarifs/${item.id}/`;
          break;
        case 'complementaires':
          endpoint = `/niveau/details-tarifs-complementaires/${item.id}/`;
          break;
        case 'vacation':
          endpoint = `/niveau/details-tarifs-vacation/${item.id}/`;
          break;
        default:
          return;
      }

      await api.delete(endpoint);
      toast.success('Élément supprimé avec succès');
      fetchTarifs(anneeEnCours?.id);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteModal({ isOpen: false, item: null, type: null });
    }
  };

  const handleOpenModal = (tarif = null) => {
    if (tarif) {
      setSelectedTarif(tarif);
      setFormData({
        prix: tarif.prix || '',
        niveau: tarif.niveau?.id || '',
        filiere: tarif.filiere?.map(f => f.id) || [],
        libelle: tarif.libelle || '',
        prix_heure: tarif.prix_heure || ''
      });
    } else {
      setSelectedTarif(null);
      setFormData({
        prix: '',
        niveau: '',
        filiere: [],
        libelle: '',
        prix_heure: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTarif(null);
    setFormData({
      prix: '',
      niveau: '',
      filiere: [],
      libelle: '',
      prix_heure: ''
    });
  };

  // Formatter le montant avec le séparateur de milliers
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount);
  };

  const handlePageChange = async (type, newPage) => {
    setPagination(prev => ({
      ...prev,
      [type]: { ...prev[type], page: newPage }
    }));
    await fetchTarifs(anneeEnCours?.id);
  };

  const renderPagination = (type) => {
    const { page, totalPages } = pagination[type];
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center mt-6 space-x-2">
        <button
          onClick={() => handlePageChange(type, page - 1)}
          disabled={page === 1}
          className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 disabled:opacity-50"
        >
          Précédent
        </button>
        <span className="px-3 py-1">
          Page {page} sur {totalPages}
        </span>
        <button
          onClick={() => handlePageChange(type, page + 1)}
          disabled={page === totalPages}
          className="px-3 py-1 rounded-md bg-gray-100 text-gray-700 disabled:opacity-50"
        >
          Suivant
        </button>
      </div>
    );
  };

  const renderEmptyState = (message) => (
    <div className="flex flex-col items-center justify-center h-64 text-gray-500">
      <AlertTriangle className="h-12 w-12 mb-4" />
      <p className="text-lg font-medium">{message}</p>
    </div>
  );

  const renderModal = () => {
    if (!isModalOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">
                {selectedTarif ? 'Modifier le tarif' : 'Ajouter un tarif'}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {activeTab === 'tarifs' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Niveau
                  </label>
                  <select
                    value={formData.niveau}
                    onChange={(e) => setFormData({ ...formData, niveau: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all"
                    required
                  >
                    <option value="">Sélectionnez un niveau</option>
                    {niveaux.map((niveau) => (
                      <option key={niveau.id} value={niveau.id}>
                        {niveau.libelle}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filières
                  </label>
                  <Select
                    isMulti
                    name="filiere"
                    options={filieres.map(f => ({ value: f.id, label: f.libelle }))}
                    value={filieres
                      .filter(f => formData.filiere.includes(f.id))
                      .map(f => ({ value: f.id, label: f.libelle }))}
                    onChange={(selected) => setFormData({
                      ...formData,
                      filiere: selected ? selected.map(option => option.value) : []
                    })}
                    styles={customSelectStyles}
                    placeholder="Sélectionnez les filières"
                    noOptionsMessage={() => "Aucune filière disponible"}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
              </>
            )}

            {activeTab === 'complementaires' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Libellé
                </label>
                <input
                  type="text"
                  value={formData.libelle}
                  onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all"
                  placeholder="Nom du tarif complémentaire"
                  required
                />
              </div>
            )}

            {activeTab === 'vacation' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Niveau
                  </label>
                  <select
                    value={formData.niveau}
                    onChange={(e) => setFormData({ ...formData, niveau: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all"
                    required
                  >
                    <option value="">Sélectionnez un niveau</option>
                    {niveaux.map((niveau) => (
                      <option key={niveau.id} value={niveau.id}>
                        {niveau.libelle}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filières (optionnel)
                  </label>
                  <Select
                    isMulti
                    name="filiere"
                    options={filieres.map(f => ({ value: f.id, label: f.libelle }))}
                    value={filieres
                      .filter(f => formData.filiere.includes(f.id))
                      .map(f => ({ value: f.id, label: f.libelle }))}
                    onChange={(selected) => setFormData({
                      ...formData,
                      filiere: selected ? selected.map(option => option.value) : []
                    })}
                    styles={customSelectStyles}
                    placeholder="Sélectionnez les filières"
                    noOptionsMessage={() => "Aucune filière disponible"}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {activeTab === 'vacation' ? 'Prix horaire (FCFA)' : 'Prix (FCFA)'}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={activeTab === 'vacation' ? formData.prix_heure : formData.prix}
                  onChange={(e) => setFormData({
                    ...formData,
                    [activeTab === 'vacation' ? 'prix_heure' : 'prix']: e.target.value
                  })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent transition-all"
                  placeholder="Montant"
                  required
                  min="0"
                />
              </div>
            </div>
          </form>

          <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3 rounded-b-xl">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-all"
            >
              Annuler
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 bg-[#202654] text-white rounded-md hover:bg-[#2d347d] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Enregistrement...
                </>
              ) : (
                selectedTarif ? 'Mettre à jour' : 'Enregistrer'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const filterData = () => {
    const filtered = {
      tarifs: tarifs.filter(tarif => 
        tarif.niveau?.libelle.toLowerCase().includes(searchQueries.tarifs.toLowerCase()) ||
        tarif.filiere?.some(f => f.libelle.toLowerCase().includes(searchQueries.tarifs.toLowerCase()))
      ),
      complementaires: tarifsComplementaires.filter(tarif =>
        tarif.libelle.toLowerCase().includes(searchQueries.complementaires.toLowerCase())
      ),
      vacation: tarifsVacation.filter(tarif =>
        tarif.niveau?.libelle.toLowerCase().includes(searchQueries.vacation.toLowerCase())
      )
    };
    setFilteredData(filtered);
  };

  const handleSearch = (type, value) => {
    setSearchQueries(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const renderSearchBar = (type) => (
    <div className="relative mb-6">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Search className="h-5 w-5 text-gray-400" />
      </div>
      <input
        type="text"
        value={searchQueries[type]}
        onChange={(e) => handleSearch(type, e.target.value)}
        className="block pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
        placeholder={`Rechercher ${type === 'tarifs' ? 'par niveau ou filière' : type === 'complementaires' ? 'par libellé' : 'par niveau'}`}
      />
    </div>
  );

  const renderVacationCard = (tarif) => (
    <div key={tarif.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{tarif.niveau?.libelle}</h3>
          {tarif.filiere && tarif.filiere.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {tarif.filiere.map(f => (
                <span
                  key={f.id}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                >
                  {f.libelle}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => handleOpenModal(tarif)}
            className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-[#202654] transition-colors duration-200"
          >
            <Edit2 className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDelete(tarif, 'vacation')}
            className="p-1 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors duration-200"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="mt-4 flex items-center">
        <Clock className="h-6 w-6 text-[#202654]" />
        <div className="ml-2">
          <div className="text-2xl font-bold text-[#202654]">
            {formatAmount(tarif.prix_heure)} FCFA/h
          </div>
        </div>
      </div>
    </div>
  );

  const renderDeleteModal = () => {
    if (!deleteModal.isOpen) return null;

    let title, description;
    switch (deleteModal.type) {
      case 'tarifs':
        title = 'Supprimer le tarif';
        description = `Êtes-vous sûr de vouloir supprimer le tarif pour ${deleteModal.item.niveau?.libelle} ?`;
        break;
      case 'complementaires':
        title = 'Supprimer le tarif complémentaire';
        description = `Êtes-vous sûr de vouloir supprimer le tarif "${deleteModal.item.libelle}" ?`;
        break;
      case 'vacation':
        title = 'Supprimer le tarif de vacation';
        description = `Êtes-vous sûr de vouloir supprimer le tarif de vacation pour ${deleteModal.item.niveau?.libelle} ?`;
        break;
      default:
        return null;
    }

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {title}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      {description}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={confirmDelete}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Supprimer
              </button>
              <button
                type="button"
                onClick={() => setDeleteModal({ isOpen: false, item: null, type: null })}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                    <DollarSign className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Gestion des Tarifs</h1>
                    <p className="mt-1 text-blue-100">
                      Gérez les tarifs de scolarité et les frais complémentaires
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleOpenModal()}
                  className="mt-4 md:mt-0 inline-flex items-center bg-white text-[#202654] px-4 py-2 rounded-lg shadow-md hover:bg-gray-100 transition-all duration-200"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Ajouter un tarif
                </button>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="p-6">
              <div className="max-w-7xl mx-auto">
                {/* En-tête avec statistiques */}
                <div className="mb-8">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Liste des tarifs</h2>
                      {anneeEnCours && (
                        <p className="mt-2 text-sm text-gray-600">
                          Année académique en cours: <span className="font-medium">{anneeEnCours.libelle}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Cartes de statistiques */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                      <div className="flex items-center">
                        <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                          <School className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600">Total Tarifs</p>
                          <p className="text-2xl font-semibold text-gray-900">{tarifs.length}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                      <div className="flex items-center">
                        <div className="p-3 rounded-full bg-green-100 text-green-600">
                          <BookOpen className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600">Tarifs Complémentaires</p>
                          <p className="text-2xl font-semibold text-gray-900">{tarifsComplementaires.length}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                      <div className="flex items-center">
                        <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                          <Clock className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600">Tarifs de Vacation</p>
                          <p className="text-2xl font-semibold text-gray-900">{tarifsVacation.length}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Onglets */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                    <nav className="flex">
                      <TabButton
                        isActive={activeTab === 'tarifs'}
                        onClick={() => setActiveTab('tarifs')}
                        icon={School}
                      >
                        Tarifs de Scolarité
                      </TabButton>
                      <TabButton
                        isActive={activeTab === 'complementaires'}
                        onClick={() => setActiveTab('complementaires')}
                        icon={BookOpen}
                      >
                        Tarifs Complémentaires
                      </TabButton>
                      <TabButton
                        isActive={activeTab === 'vacation'}
                        onClick={() => setActiveTab('vacation')}
                        icon={Clock}
                      >
                        Tarifs de Vacation
                      </TabButton>
                    </nav>
                  </div>

                  {/* Contenu des onglets */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 transition-all duration-300">
                    {loading ? (
                      <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
                      </div>
                    ) : (
                      <>
                        {renderSearchBar(activeTab)}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {activeTab === 'tarifs' && (
                            filteredData.tarifs.length > 0 ? (
                              filteredData.tarifs.map((tarif) => (
                                <div key={tarif.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <h3 className="text-lg font-medium text-gray-900">{tarif.niveau?.libelle}</h3>
                                      {tarif.filiere && tarif.filiere.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                          {tarif.filiere.map(f => (
                                            <span
                                              key={f.id}
                                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                                            >
                                              {f.libelle}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() => handleOpenModal(tarif)}
                                        className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-[#202654] transition-colors duration-200"
                                      >
                                        <Edit2 className="h-5 w-5" />
                                      </button>
                                      <button
                                        onClick={() => handleDelete(tarif, 'tarifs')}
                                        className="p-1 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors duration-200"
                                      >
                                        <Trash2 className="h-5 w-5" />
                                      </button>
                                    </div>
                                  </div>
                                  <div className="mt-4 flex items-center">
                                    <DollarSign className="h-6 w-6 text-[#202654]" />
                                    <div className="ml-2">
                                      <div className="text-2xl font-bold text-[#202654]">
                                        {formatAmount(tarif.prix)} FCFA
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : renderEmptyState("Aucun tarif n'a été trouvé")
                          )}

                          {activeTab === 'complementaires' && (
                            filteredData.complementaires.length > 0 ? (
                              filteredData.complementaires.map((tarif) => (
                                <div key={tarif.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <h3 className="text-lg font-medium text-gray-900">{tarif.libelle}</h3>
                                      <div className="mt-1 text-sm text-gray-500">
                                        {tarif.annee?.libelle}
                                      </div>
                                    </div>
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() => handleOpenModal(tarif)}
                                        className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-[#202654] transition-colors duration-200"
                                      >
                                        <Edit2 className="h-5 w-5" />
                                      </button>
                                      <button
                                        onClick={() => handleDelete(tarif, 'complementaires')}
                                        className="p-1 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors duration-200"
                                      >
                                        <Trash2 className="h-5 w-5" />
                                      </button>
                                    </div>
                                  </div>
                                  <div className="mt-4 flex items-center">
                                    <DollarSign className="h-6 w-6 text-[#202654]" />
                                    <div className="ml-2">
                                      <div className="text-2xl font-bold text-[#202654]">
                                        {formatAmount(tarif.prix)} FCFA
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : renderEmptyState("Aucun tarif n'a été trouvé")
                          )}

                          {activeTab === 'vacation' && (
                            filteredData.vacation.length > 0 ? (
                              filteredData.vacation.map(tarif => renderVacationCard(tarif))
                            ) : renderEmptyState("Aucun tarif de vacation n'a été trouvé")
                          )}
                        </div>
                        {renderPagination(activeTab)}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modals existants */}
        {renderModal()}
        {renderDeleteModal()}
      </div>
    </Layout>
  );
};

export default TarificationPage;
